import React, { useCallback, useContext, useState } from "react";
import { RouteStatusContext } from "./context/RouteStatusContext";
import { RouteFilterContext } from "./context/RouteFilterContext";
import { AssociateContext } from "../associate/context/AssociateContext";
import { useDeviceLayout } from "../common/context/DeviceLayoutContext";
import {
  applyGlobalStageLocationTypeFilter,
  applyInlineStagingLocationFilter,
  applyInlineStageTimeFilter,
  filterOption,
} from "./utils/FilterUtil";
import { getDocumentURL } from "../handler/ParentDataHandler";
import {
  convertEpochToLocaleFormat,
  convertMillisecondsToHHMM,
  naIfDefault,
} from "../util/Util";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import { FormattedMessage } from "react-intl";
import Link from "@amzn/meridian/link";
import Tooltip from "@amzn/meridian/tooltip";
import Input from "@amzn/meridian/input";
import Select, { SelectOption } from "@amzn/meridian/select";
import Row from "@amzn/meridian/row";
import Icon from "@amzn/meridian/icon";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";

import { getPicklistStatusColor, STAGED } from "./utils/EntityStatusManager";
import Tag from "@amzn/meridian/tag";
import ScrollableElementWrapper from "../CommonModels/ScrollableElementWrapper";
import Pagination from "@amzn/meridian/pagination";
import Text from "@amzn/meridian/text";
import CartIcon from "../assets/cart-icon.png";
import {
  applyProductTypeFilter,
  getPicklistProgress,
  getPicklistLocation,
  renderWorkGroupIcons,
  getPicklistStageTime,
  renderRouteSpeed,
  isStageByTimeBreached,
  isRouteAtRisk,
} from "./utils/Util";
import {
  ASSOCIATE,
  COLLAPSE_TABLE,
  DRIVER_TYPE,
  EXPAND_TABLE,
  FALLBACK_DASH,
  FALLBACK_ELLIPSIS,
  LOCATION,
  MIN_IN_STATUS,
  PROGRESS,
  ROUTE_ID,
  ROUTE_SPEED,
  ROUTE_SPEED_LABELS,
  STAGE_BY_TIME,
  STAGE_TIME,
  STATUS,
  CLUSTER_FILTER,
  ROUTE_CODE_FILTER,
  DRIVER_TYPE_FILTER,
  DEPARTURE_FILTER,
  ROUTE_SLA_FILTER,
  STATUS_FILTER,
  ROUTE_STATUS_FILTER,
  DELIVERY_SPEED_FILTER,
  ROUTE_SPEED_FILTER,
  DRIVER_FILTER,
  STAGE_DURATION_FILTER,
} from "./utils/Constants";
import { SET_ASSOCIATE } from "./utils/ReducerActionTypes";

const RouteViewTable = () => {
  const { viewModel } = useContext(RouteStatusContext);
  const { viewModelF } = useContext(RouteFilterContext);
  const { associateModelDispatcher } = useContext(AssociateContext);
  const [expandedRoutes, setExpandedRoutes] = useState([]);
  const { spacingInset_shorthand2 } = useDeviceLayout();

  /** Pagination State **/

  const [currentPage, setCurrentPage] = useState(1);

  /** Sorting **/

  const [sortColumn, setSortColumn] = useState("routeSLA");
  const [sortDirection, setSortDirection] = useState("ascending");
  const onSort = useCallback(({ sortColumn, sortDirection }) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  }, []);

  /**Filter stage**/

  const [routeCodeFilter, setRouteCodeFilter] = useState("");
  const [driverTypeFilter, setDriverTypeFilter] = useState("");
  const [routeStatus, setRouteStatus] = useState("");
  const [stageTime, setStageTime] = useState("");
  const [routeSLA, setRouteSLA] = useState("");
  const [location, setLocation] = useState("");
  const [driverFilter] = useState("");
  const [associateFilter, setAssociateFilter] = useState("");
  const [stageDurationFilter, setStageDurationFilter] = useState("");
  const [routeSpeedFilter, setRouteSpeedFilter] = useState("");
  const [allExpanded, setAllExpanded] = useState(false);

  /** Inline Filters **/

  const inLineFilter = (route, filter, key) => {
    if (filter.length === 0) return true;

    // Check route-level property
    if (
      route[key] != null &&
      route[key].toString().toLowerCase().includes(filter.toLowerCase())
    ) {
      return true;
    }

    // Check picklists if they exist
    if (route.picklists && route.picklists.length > 0) {
      return route.picklists.some(
        (picklist) =>
          picklist[key] != null &&
          picklist[key].toString().toLowerCase().includes(filter.toLowerCase()),
      );
    }

    return false;
  };

  const inLineContainsFilter = (route, filter, key) => {
    return (
      filter.length === 0 ||
      (route[key] != null &&
        route[key].toString().toLowerCase().includes(filter.toLowerCase()))
    );
  };

  const routeSpeedFilterFunc = (route, filter) => {
    if (filter.length === 0) return true;
    if (
      filter === ROUTE_SPEED_LABELS.ONE_HOUR &&
      route.routeSpeed === ROUTE_SPEED.RUSH_1_HR
    )
      return true;
    if (
      filter === ROUTE_SPEED_LABELS.THREE_HOUR &&
      route.routeSpeed === ROUTE_SPEED.RUSH_3_HR
    )
      return true;
    if (
      filter === ROUTE_SPEED_LABELS.STANDARD_SPEED &&
      route.routeSpeed === ROUTE_SPEED.STANDARD
    )
      return true;
    return false;
  };

  const associateFilterFunc = (route, filter) => {
    if (filter.length === 0) return true;
    // Check if any picklist has an associate matching the filter
    if (route.picklists && route.picklists.length > 0) {
      return route.picklists.some(
        (picklist) =>
          picklist.associates &&
          picklist.associates.some(
            (assoc) =>
              assoc.alias &&
              assoc.alias.toLowerCase().includes(filter.toLowerCase()),
          ),
      );
    }
  };

  const routeStatusFilterFunc = (route, filter) => {
    if (filter.length === 0) return true;

    // Check route-level status
    if (
      route.routeStatus &&
      route.routeStatus.toLowerCase().includes(filter.toLowerCase())
    ) {
      return true;
    }

    // Check picklist-level statuses
    if (route.picklists && route.picklists.length > 0) {
      return route.picklists.some(
        (picklist) =>
          picklist.status &&
          picklist.status.toLowerCase().includes(filter.toLowerCase()),
      );
    }

    return false;
  };

  let routeList = viewModel.routeList;
  routeList.sort((a, b) => {
    if (a[sortColumn] < b[sortColumn]) {
      return sortDirection === "ascending" ? -1 : 1;
    }
    if (a[sortColumn] > b[sortColumn]) {
      return sortDirection === "ascending" ? 1 : -1;
    }
    return 0;
  });

  const toggleAllRoutes = () => {
    if (allExpanded) {
      setExpandedRoutes([]);
    } else {
      const allRouteIds = routeList.map((route) => route.routeId);
      setExpandedRoutes(allRouteIds);
    }
    setAllExpanded(!allExpanded);
  };

  const applyAllFilters = (routes) => {
    return routes
      .filter((route) =>
        filterOption(route, CLUSTER_FILTER, ROUTE_CODE_FILTER, viewModelF),
      )
      .filter((route) =>
        filterOption(route, DRIVER_TYPE_FILTER, DRIVER_TYPE_FILTER, viewModelF),
      )
      .filter((route) =>
        filterOption(route, DEPARTURE_FILTER, ROUTE_SLA_FILTER, viewModelF),
      )
      .filter((route) =>
        filterOption(route, STATUS_FILTER, ROUTE_STATUS_FILTER, viewModelF),
      )
      .filter((route) =>
        filterOption(
          route,
          DELIVERY_SPEED_FILTER,
          ROUTE_SPEED_FILTER,
          viewModelF,
        ),
      )
      .filter((route) =>
        applyGlobalStageLocationTypeFilter(route, viewModelF.stageLocationType),
      )
      .filter((route) => applyProductTypeFilter(viewModelF, route))
      .filter((route) =>
        inLineFilter(route, routeCodeFilter, ROUTE_CODE_FILTER),
      )
      .filter((route) =>
        inLineContainsFilter(route, driverTypeFilter, DRIVER_TYPE_FILTER),
      )
      .filter((route) => routeStatusFilterFunc(route, routeStatus))
      .filter((route) => associateFilterFunc(route, associateFilter))
      .filter((route) => applyInlineStageTimeFilter(route, stageTime))
      .filter((route) => inLineFilter(route, routeSLA, ROUTE_SLA_FILTER))
      .filter((route) => applyInlineStagingLocationFilter(route, location))
      .filter((route) => inLineFilter(route, driverFilter, DRIVER_FILTER))
      .filter((route) =>
        inLineFilter(route, stageDurationFilter, STAGE_DURATION_FILTER),
      )
      .filter((route) => routeSpeedFilterFunc(route, routeSpeedFilter));
  };

  routeList = applyAllFilters(routeList);

  const itemOnPage = 50;
  const pageSize = Math.ceil(routeList.length / itemOnPage);
  const startPage = (currentPage - 1) * itemOnPage;
  const endPage =
    startPage + itemOnPage > routeList.length - 1
      ? routeList.length - 1
      : startPage + itemOnPage - 1;
  if (startPage > routeList.length) {
    setCurrentPage(1);
  }
  const onRouteClick = (route) => {
    getDocumentURL()
      .then((result) => {
        const params = [
          "routestatus",
          naIfDefault(route.routeId),
          naIfDefault(encodeURIComponent(route.routeStatus)),
          naIfDefault(encodeURIComponent(route.stagingLocation)),
          naIfDefault(route.hazmatWeight),
          naIfDefault(route.picker),
          naIfDefault(route.routeCode),
          naIfDefault(route.stationCode),
        ];
        const url = new URL(result.replace("routestatus", "pick"));
        const shareableLink = "/routeDetail/" + params.join("/");
        url.searchParams.set("shareableLink", shareableLink);
        window.open(url.href, "_parent");
      })
      .catch((error) => {
        console.error("Error opening route details:", error);
      });
  };

  const expandRoute = (routeId) => {
    if (isRouteExpanded(routeId)) {
      setExpandedRoutes(
        expandedRoutes.filter((expandedRoute) => expandedRoute !== routeId),
      );
    } else {
      setExpandedRoutes(expandedRoutes.concat(routeId));
    }
  };

  const isRouteExpanded = (routeId) => {
    return expandedRoutes.indexOf(routeId) !== -1;
  };

  // Details of other rows to be added here
  const renderHeaderRow = () => {
    return (
      <TableRow>
        <TableCell sortColumn="routeCode" alignmentHorizontal="center">
          <FormattedMessage
            id="routeStatusTable.routeId"
            defaultMessage={ROUTE_ID}
          />
        </TableCell>
        <TableCell sortColumn="routeSLA" alignmentHorizontal="center">
          <FormattedMessage
            id="routeStatusTable.stageByTime"
            defaultMessage={STAGE_BY_TIME}
          />
        </TableCell>
        <TableCell sortColumn="routeStatus" alignmentHorizontal="center">
          <FormattedMessage
            id="routeStatusTable.status"
            defaultMessage={STATUS}
          />
        </TableCell>
        <TableCell sortColumn="stageProgess" alignmentHorizontal="center">
          <FormattedMessage
            id="routeStatusTable.progress"
            defaultMessage={PROGRESS}
          />
        </TableCell>
        <TableCell sortColumn="stageDuration" alignmentHorizontal="center">
          <FormattedMessage
            id="routeStatusTable.stageDuration"
            defaultMessage={MIN_IN_STATUS}
          />
        </TableCell>
        <TableCell sortColumn="picker" alignmentHorizontal="center">
          <FormattedMessage
            id="routeStatusTable.associate"
            defaultMessage={ASSOCIATE}
          />
        </TableCell>
        <TableCell sortColumn="stagingLocation" alignmentHorizontal="center">
          <FormattedMessage
            id="routeStatusTable.location"
            defaultMessage={LOCATION}
          />
        </TableCell>
        <TableCell sortColumn="stageTime" alignmentHorizontal="center">
          <FormattedMessage
            id="routeStatusTable.stageTime"
            defaultMessage={STAGE_TIME}
          />
        </TableCell>
        <TableCell sortColumn="driverType" alignmentHorizontal="center">
          <FormattedMessage
            id="routeStatusTable.type"
            defaultMessage={DRIVER_TYPE}
          />
        </TableCell>
        <TableCell sortColumn="speed" alignmentHorizontal="center">
          <img
            src={CartIcon}
            alt="Speed"
            style={{ width: "24px", height: "24px" }}
          />
        </TableCell>
      </TableRow>
    );
  };

  const onAssociateClick = (picker) => {
    const associate = [picker];
    associateModelDispatcher({ type: SET_ASSOCIATE, data: associate });
  };

  const renderAssociateName = (associate) => {
    if (associate && associate.alias && associate.alias !== FALLBACK_ELLIPSIS) {
      return (
        <Link
          onClick={() => {
            onAssociateClick(associate.alias);
          }}
          type="secondary"
        >
          {associate.alias}
        </Link>
      );
    } else {
      return "-";
    }
  };
  const renderStageDuration = (route) => {
    if (route.stageError) {
      return (
        <Tooltip position="top" title={route.stageError}>
          <Text>{route.stageDuration}</Text>
        </Tooltip>
      );
    }
    return route.stageDuration;
  };

  const renderInlineFilterRow = () => {
    return (
      <TableRow>
        <TableCell>
          <Input
            type="text"
            size="small"
            value={routeCodeFilter}
            onChange={setRouteCodeFilter}
          />
        </TableCell>
        <TableCell>
          <Input
            type="text"
            size="small"
            value={routeSLA}
            onChange={setRouteSLA}
          />
        </TableCell>
        <TableCell>
          <Input
            type="text"
            size="small"
            value={routeStatus}
            onChange={setRouteStatus}
          />
        </TableCell>
        <TableCell></TableCell>
        <TableCell>
          <Input
            type="text"
            size="small"
            value={stageDurationFilter}
            onChange={setStageDurationFilter}
          />
        </TableCell>
        <TableCell>
          <Input
            type="text"
            size="small"
            value={associateFilter}
            onChange={setAssociateFilter}
          />
        </TableCell>
        <TableCell>
          <Input
            type="text"
            size="small"
            value={location}
            onChange={setLocation}
          />
        </TableCell>
        <TableCell>
          <Input
            type="text"
            size="small"
            value={stageTime}
            onChange={setStageTime}
          />
        </TableCell>
        <TableCell>
          <Input
            type="text"
            size="small"
            value={driverTypeFilter}
            onChange={setDriverTypeFilter}
          />
        </TableCell>
        <TableCell>
          <Select
            size="small"
            value={routeSpeedFilter}
            onChange={setRouteSpeedFilter}
          >
            <SelectOption value="" label={"All"}></SelectOption>
            <SelectOption
              value={ROUTE_SPEED_LABELS.ONE_HOUR}
              label={ROUTE_SPEED_LABELS.ONE_HOUR}
            ></SelectOption>
            <SelectOption
              value={ROUTE_SPEED_LABELS.THREE_HOUR}
              label={ROUTE_SPEED_LABELS.THREE_HOUR}
            ></SelectOption>
            <SelectOption
              value={ROUTE_SPEED_LABELS.STANDARD_SPEED}
              label={ROUTE_SPEED_LABELS.STANDARD_SPEED}
            ></SelectOption>
          </Select>
        </TableCell>
      </TableRow>
    );
  };

  const renderRouteRow = (route) => {
    const routeRow = (
      <TableRow
        key={route.routeId}
        backgroundColor={isRouteAtRisk(route) ? "#FFEDEB" : "default"}
      >
        <TableCell alignmentVertical="top">
          <Row>
            <Link
              data-expand-link={route.routeId}
              onClick={() => expandRoute(route.routeId)}
              type={"secondary"}
              size={"small"}
            >
              {isRouteExpanded(route.routeId) ? (
                <Icon tokens={chevronDownSmallTokens} />
              ) : (
                <Icon tokens={chevronRightSmallTokens} />
              )}
            </Link>
            <Link
              onClick={() => {
                onRouteClick(route);
              }}
              type="secondary"
            >
              {route.routeCode}
            </Link>
          </Row>
        </TableCell>
        <TableCell>
          <Tooltip
            position={"top"}
            title={convertEpochToLocaleFormat(route.criticalPullTime)}
          >
            <Text>{route.routeSLA}</Text>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip position="top" title={route.routeStatus}>
            <Tag type={getPicklistStatusColor(route.routeStatus)}>
              {route.routeStatus && route.routeStatus.length > 11
                ? route.routeStatus.substr(0, 10) + FALLBACK_ELLIPSIS
                : route.routeStatus}
            </Tag>
          </Tooltip>
        </TableCell>
        <TableCell>{route.routeProgress}</TableCell>
        <TableCell>{renderStageDuration(route)}</TableCell>
        <TableCell>{}</TableCell>
        <TableCell>{}</TableCell>
        <TableCell>{convertMillisecondsToHHMM(route.stageTime)}</TableCell>
        <TableCell>{route.driverType}</TableCell>
        <TableCell>{renderRouteSpeed(route.routeSpeed)}</TableCell>
      </TableRow>
    );
    const picklistRows = renderPicklistRows(route);
    return [routeRow, ...picklistRows];
  };

  const renderPicklistRows = (route) => {
    if (!isRouteExpanded(route.routeId) || !Array.isArray(route.picklists)) {
      return [];
    }
    return route.picklists.map((picklist, index) =>
      renderPicklistRow(route, picklist, index),
    );
  };

  const renderPicklistRow = (route, picklist, index) => {
    const associate = picklist.associates && picklist.associates[0];
    const pickingDuration =
      associate && associate.pickingDuration
        ? convertMillisecondsToHHMM(associate.pickingDuration)
        : FALLBACK_ELLIPSIS;
    const stageByTime = picklist.stageByTime
      ? convertMillisecondsToHHMM(picklist.stageByTime)
      : FALLBACK_ELLIPSIS;
    const picklistWorkGroupIcons =
      renderWorkGroupIcons(picklist) || FALLBACK_DASH;
    const picklistTime = getPicklistStageTime(picklist);
    const picklistLocation = getPicklistLocation(picklist);
    const picklistStatus =
      picklist && picklist.status ? picklist.status : FALLBACK_DASH;
    const isPicklistAtRisk =
      picklistStatus !== STAGED &&
      isStageByTimeBreached(picklist.stageByTime, picklist.status);
    return (
      <TableRow
        key={`${route.routeId}_picklist_${index}`}
        backgroundColor={isPicklistAtRisk ? "#FFEDEB" : "default"}
      >
        <TableCell />
        <TableCell>
          <Tooltip
            position={"top"}
            title={convertEpochToLocaleFormat(picklist.stageByTime)}
          >
            <Text>{stageByTime}</Text>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tag type={getPicklistStatusColor(picklistStatus)}>
            {picklistStatus}
          </Tag>
        </TableCell>
        <TableCell>{getPicklistProgress(picklist)}</TableCell>
        <TableCell>{pickingDuration}</TableCell>
        <TableCell>{renderAssociateName(associate)}</TableCell>
        <TableCell>{picklistLocation}</TableCell>
        <TableCell>{picklistTime}</TableCell>
        <TableCell>{}</TableCell>
        <TableCell>{picklistWorkGroupIcons}</TableCell>
      </TableRow>
    );
  };

  return (
    <div>
      <ScrollableElementWrapper>
        <Row
          width="100%"
          widths="fill"
          spacingInset={spacingInset_shorthand2}
          className="enable-scroll-x"
        >
          <div>
            <Row
              width="100%"
              alignmentHorizontal="right"
              spacingInset="none medium small"
            >
              <Link type="secondary" onClick={toggleAllRoutes}>
                {allExpanded ? COLLAPSE_TABLE : EXPAND_TABLE}
              </Link>
            </Row>
            <Table
              showStripes={false}
              showDividers={true}
              headerRows={1}
              headerColumns={1}
              fixHeaderRows={true}
              stickyHeaderColumn={true}
              stickyHeaderRow={true}
              spacing={"small"}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
            >
              {renderHeaderRow()}
              {renderInlineFilterRow()}
              {routeList.map((route, key) =>
                key >= startPage && key <= endPage
                  ? renderRouteRow(route)
                  : null,
              )}
            </Table>

            {routeList.length > 0 ? (
              <Row
                width="100%"
                alignmentHorizontal="center"
                spacingInset="medium none none"
              >
                <Pagination
                  numberOfPages={pageSize}
                  onChange={setCurrentPage}
                  currentPage={currentPage}
                />
              </Row>
            ) : null}
          </div>
        </Row>
      </ScrollableElementWrapper>
    </div>
  );
};
export default RouteViewTable;



// WEBPACK FOOTER //
// ./src/components/RouteStatus/RouteViewTable.js